import React from "react";
import {AppStore} from "./AppStore";
import {DisposeUtils} from "../utils/DisposeUtils";
import {ErrorUtils} from "../utils/ErrorUtils";
import {proto} from "../proto/messages";
import {makeAutoObservable} from "mobx";
import {recordings} from "../proto/compiled";
import {Subject} from "rxjs";

export const RecordingsStoreContext = React.createContext<RecordingsStore | undefined>(undefined);
export const MyRecordingsStoreContext = React.createContext<RecordingsStore | undefined>(undefined);
export const OrganizationRecordingsStoreContext = React.createContext<RecordingsStore | undefined>(undefined);

export class RecordingsStore {
  private disposer = new DisposeUtils();
  private app: AppStore;
  private scope: recordings.RecordingListScope;
  private pageSize?: number;
  private loadPending = false;
  loading = false;
  recordings?: recordings.IListRecordingsResponse;

  // Static stream for recording updates
  static recordingUpdated = new Subject<recordings.IRecording>();
  // Static stream for when a new recording is created (triggers reload on all stores)
  static recordingCreated = new Subject<void>();
  // Static stream for when a recording is deleted (triggers reload on all stores)
  static recordingDeleted = new Subject<string>();

  constructor(app: AppStore, scope: recordings.RecordingListScope, pageSize?: number) {
    this.app = app;
    this.scope = scope;
    this.pageSize = pageSize;
    this.load();
    makeAutoObservable(this);

    // Subscribe to recording updates
    this.disposer.add(
      RecordingsStore.recordingUpdated.subscribe((updatedRecording) => {
        if (this.recordings?.recordings) {
          const index = this.recordings.recordings.findIndex(r => r.id === updatedRecording.id);
          if (index >= 0) {
            Object.assign(this.recordings.recordings[index], updatedRecording);
          }
        }
      })
    );

    // Subscribe to new recording notifications
    this.disposer.add(
      RecordingsStore.recordingCreated.subscribe(() => {
        this.load();
      })
    );

    // Subscribe to recording deletion notifications
    this.disposer.add(
      RecordingsStore.recordingDeleted.subscribe((deletedId) => {
        if (this.recordings?.recordings) {
          this.recordings.recordings = this.recordings.recordings.filter(r => r.id !== deletedId);
        }
      })
    );
  }

  dispose() {
    this.disposer.dispose();
  }

  async load() {
    if (this.loading) {
      this.loadPending = true;
      return;
    }
    this.loading = true;
    await this.app.socketApiConnectedAndAuthenticated;
    try {
      this.recordings = (await this.app.socketApi.fetch(
        proto.TxListRecordings.create({
          scope: this.scope,
          pagination: this.recordings?.pagination ?? {pageSize: this.pageSize}
        }),
        new proto.RxListRecordingsResponse(),
      )).proto;
    } catch (e) {
      ErrorUtils.fromFetch(e);
    }
    this.loading = false;
    if (this.loadPending) {
      this.loadPending = false;
      this.load();
    }
  }

  async deleteRecording(id: string) {
    await this.app.socketApiConnectedAndAuthenticated;
    const status = await this.app.socketApi.sendMessage(
      proto.TxDeleteRecording.create({id}),
      {ack: true}
    );
    if (ErrorUtils.fromFetch(status)) {
      RecordingsStore.recordingDeleted.next(id);
    }
  }
}